import { useState, useMemo } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { FileText, DollarSign, Download, Mail, Loader2, Info, FileDown, ChevronDown, Check, User } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMovimientosCliente, useCuentas, useCrearMovimiento, useActualizarMovimiento, useEliminarMovimiento, movimientosKeys } from '@/hooks/useMovimientos'
import { balanceForwardKeys } from '@/hooks/useBalanceForward'
import { useQueryClient } from '@tanstack/react-query'
import { MovimientosTable } from '@/components/movimientos/MovimientosTable'
import { MovimientoFormDialog } from '@/components/movimientos/MovimientoFormDialog'
import { RegistrarPagoDialog } from '@/components/movimientos/registrar-pago'
import { PeriodPresets } from '@/components/movimientos/PeriodPresets'
import { ComprobanteViewer } from '@/components/movimientos/ComprobanteViewer'
import type { MovimientoFormValues } from '@/components/movimientos/MovimientoForm'
import type { MovimientoConCliente } from '@/types/database'
import type { VistaSaldoClienteConImpuestos } from '@/services/clientesService'
import { formatCurrency, formatDate, formatearCUIT } from '@/lib/utils'
import { calcularTotales, sortCronologico } from '@/lib/movimientos-utils'
import { useAuth0 } from '@auth0/auth0-react'
import { emailService } from '@/services/emailService'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import { movimientosService } from '@/services/movimientosService'
import { useBalanceForward } from '@/hooks/useBalanceForward'
import { useDescargarEstadoCuentaPDF } from '@/hooks/useDescargarEstadoCuentaPDF'
import { useRegistrarEnvioManual, useUltimosEnviosEstadoCuenta } from '@/hooks/useEmailLog'
import { MarcarEnvioManualDialog, type CanalEnvioManual } from './MarcarEnvioManualDialog'
import { armarFilasMovimientos, resumirPorPeriodo } from '@/lib/estado-cuenta-utils'
import { toast } from 'sonner'
import { usePermissions } from '@/hooks/usePermissions'
import { handleMutationError } from '@/lib/permisos-guard'

interface EstadoCuentaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cliente: VistaSaldoClienteConImpuestos | null
  /** Abre la ficha 360 del cliente (tab Cuenta corriente) — ORR-141. */
  onVerFicha?: () => void
}

export function EstadoCuentaDialog({
  open,
  onOpenChange,
  cliente,
  onVerFicha,
}: EstadoCuentaDialogProps) {
  const [dialogMovimientoOpen, setDialogMovimientoOpen] = useState(false)
  const [movimientoAEditar, setMovimientoAEditar] = useState<MovimientoConCliente | null>(null)
  const [movimientoAEliminar, setMovimientoAEliminar] = useState<MovimientoConCliente | null>(null)
  const [enviandoEmail, setEnviandoEmail] = useState(false)
  const [mostrarEnvioManual, setMostrarEnvioManual] = useState(false)
  const [mostrarConfirmEnvio, setMostrarConfirmEnvio] = useState(false)
  const [mostrarRegistrarPago, setMostrarRegistrarPago] = useState(false)
  const [comprobanteAVer, setComprobanteAVer] = useState<string | null>(null)
  const [periodoFiltro, setPeriodoFiltro] = useState<{ desde: string; hasta: string } | null>(null)
  const [soloPendientes, setSoloPendientes] = useState(true)

  const { user, getAccessTokenSilently } = useAuth0()
  const { organizacionId } = useOrganizacion()
  const { puede } = usePermissions()
  const { data: configEstudio } = useConfiguracionEstudio()
  const queryClient = useQueryClient()
  const clienteId = cliente?.id || ''
  const { data: movimientos = [], isLoading } = useMovimientosCliente(clienteId, periodoFiltro?.desde, periodoFiltro?.hasta)
  const { data: balanceForward } = useBalanceForward(clienteId, periodoFiltro?.desde)
  const { data: cuentas = [] } = useCuentas()
  const crearMovimiento = useCrearMovimiento()
  const actualizarMovimiento = useActualizarMovimiento()
  const eliminarMovimiento = useEliminarMovimiento()
  const { descargar: descargarPDF, generando: generandoPDF } = useDescargarEstadoCuentaPDF()
  const registrarEnvioManual = useRegistrarEnvioManual()
  const { data: ultimosEnvios } = useUltimosEnviosEstadoCuenta(clienteId ? [clienteId] : [], organizacionId ?? undefined)
  const fechaUltimoEnvio = clienteId ? ultimosEnvios?.[clienteId] : undefined

  // Filter pending-only if toggle is on
  const movimientosFiltrados = useMemo(() => {
    if (!soloPendientes) return movimientos
    return movimientos.filter(mov => {
      if (mov.tipo === 'Deuda') {
        return mov._aplicacionEstado === 'pendiente' || mov._aplicacionEstado === 'parcial'
      }
      if (mov.tipo === 'Pago') {
        return mov._aplicacionEstado === 'sin_aplicar' || mov._aplicacionEstado === 'parcial'
      }
      return true
    })
  }, [movimientos, soloPendientes])

  // Calcular totales
  const { totalDebe, totalHaber, saldo } = calcularTotales(movimientosFiltrados)

  // ORR-224: resumen por período para el PDF (opción A). Se arma sobre `movimientos`
  // SIN el filtro por movimiento: en el resumen, "solo pendientes" se aplica por PERÍODO.
  // Filtrar antes descartaría las deudas ya canceladas y dejaría el Debe del mes incompleto.
  const resumen = useMemo(
    () => resumirPorPeriodo(movimientos, { soloPendientes }),
    [movimientos, soloPendientes],
  )

  if (!cliente) return null

  const handleNuevaDeuda = () => {
    setMovimientoAEditar(null)
    setDialogMovimientoOpen(true)
  }

  const handleNuevoPago = () => {
    // Usar el nuevo dialog inteligente para registro de pagos
    setMostrarRegistrarPago(true)
  }

  const handleSubmitMovimiento = async (data: MovimientoFormValues) => {
    try {
      if (movimientoAEditar) {
        // Editar movimiento existente
        await actualizarMovimiento.mutateAsync({
          id: movimientoAEditar.id,
          data: {
            concepto: data.concepto,
            fecha: data.fecha,
            debe: data.debe,
            haber: data.haber,
            tipo: data.tipo,
            tipo_deuda: data.tipo_deuda,
            servicio_id: data.servicio_id,
            cuenta_id: data.cuenta_id,
            mes_anio: data.mes_anio,
            observaciones: data.observaciones,
          },
        })
        toast.success('Movimiento actualizado correctamente')
        setMovimientoAEditar(null)
        setDialogMovimientoOpen(false)
      } else {
        // Crear nuevo movimiento
        await crearMovimiento.mutateAsync({
          cliente_id: cliente.id,
          concepto: data.concepto,
          fecha: data.fecha,
          debe: data.debe,
          haber: data.haber,
          monto: (data.debe ?? 0) + (data.haber ?? 0),
          tipo: data.tipo,
          tipo_deuda: data.tipo_deuda,
          servicio_id: data.servicio_id,
          cuenta_id: data.cuenta_id,
          mes_anio: data.mes_anio,
          observaciones: data.observaciones,
          created_by: user?.email ?? 'sistema',
        })

        toast.success('Movimiento creado correctamente')
        setDialogMovimientoOpen(false)
      }
    } catch (err) {
      handleMutationError(err, movimientoAEditar ? 'Error al actualizar el movimiento' : 'Error al crear el movimiento')
    }
  }

  const handleEditarMovimiento = (movimiento: MovimientoConCliente) => {
    setMovimientoAEditar(movimiento)
    setDialogMovimientoOpen(true)
  }

  const handleEliminarMovimiento = (movimiento: MovimientoConCliente) => {
    setMovimientoAEliminar(movimiento)
  }

  const handleVerComprobante = (movimiento: MovimientoConCliente) => {
    const docId = movimiento._adjuntoDocumentoId
    if (!docId) return
    setComprobanteAVer(docId)
  }

  const confirmarEliminarMovimiento = async () => {
    if (!movimientoAEliminar) return

    try {
      await eliminarMovimiento.mutateAsync(movimientoAEliminar.id)
      toast.success('Movimiento eliminado correctamente')
      setMovimientoAEliminar(null)
    } catch (err) {
      handleMutationError(err, 'Error al eliminar el movimiento')
    }
  }

  const handleCerrarDialogMovimiento = () => {
    setDialogMovimientoOpen(false)
    setMovimientoAEditar(null)
  }

  const handleExportar = () => {
    const filename = `estado-cuenta-${(cliente.nombre || 'cliente').replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`
    movimientosService.descargarCSV(movimientosFiltrados, { neto: true }, filename)
    toast.success('Estado de cuenta exportado')
  }

  const handleExportarPDF = async () => {
    try {
      // ORR-272: el detalle por movimiento vuelve a ser la tabla principal del PDF.
      const { filas: movsPDF, totales: totalesMovimientos } = armarFilasMovimientos(
        [...movimientosFiltrados].sort(sortCronologico),
        balanceForward?.saldo ?? 0,
      )

      const filename = `estado-cuenta-${(cliente.nombre || 'cliente').replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
      await descargarPDF({
        cliente: {
          nombre: cliente.nombre || 'Cliente',
          cuit: cliente.cuit != null ? formatearCUIT(String(cliente.cuit)) : undefined,
        },
        periodo: periodoFiltro ?? undefined,
        balanceForward: balanceForward?.saldo,
        movimientos: movsPDF,
        totalesMovimientos,
        periodos: resumen.filas,
        totales: resumen.totales,
      }, filename)
      toast.success('PDF exportado correctamente')
    } catch (err) {
      console.error('Error generando PDF:', err)
      toast.error('Error al generar el PDF')
    }
  }

  const handleMarcarEnvioManual = async (canal: CanalEnvioManual, notas: string) => {
    if (!cliente || !organizacionId) return
    // ORR-198: marcar como enviado es acción de contador, igual que enviar por mail (hermano de B1)
    if (!puede.enviarEstadoCuenta) {
      toast.error('No tenés permiso para registrar el envío del estado de cuenta')
      return
    }
    try {
      await registrarEnvioManual.mutateAsync({
        organizacionId,
        clienteId: cliente.id ?? '',
        tipo: 'estado_cuenta',
        destinatarioEmail: cliente.email || '',
        destinatarioNombre: cliente.nombre || 'Cliente',
        canal,
        notas,
        enviadoPorEmail: user?.email ?? '',
      })
      setMostrarEnvioManual(false)
    } catch (err) {
      handleMutationError(err, 'No se pudo registrar el envío manual')
    }
  }

  const handleEnviarEmail = async () => {
    // ORR-200: flag propio enviarEstadoCuenta (reemplaza el proxy editarCliente de B1)
    if (!puede.enviarEstadoCuenta) {
      toast.error('No tenés permiso para enviar el estado de cuenta')
      return
    }
    if (!cliente.email) {
      toast.error('El cliente no tiene email configurado')
      return
    }
    if (!organizacionId) {
      toast.error('No se pudo identificar la organización. Recargá la página.')
      return
    }

    setEnviandoEmail(true)
    try {
      const movimientosParaEmail = movimientos
        .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
        .map((mov) => ({
          fecha: mov.fecha,
          concepto: mov.concepto,
          debe: mov.debe ?? 0,
          haber: mov.haber ?? 0,
        }))

      const token = await getAccessTokenSilently()
      const result = await emailService.enviarEstadoCuenta(
        token,
        { id: cliente.id ?? '', nombre: cliente.nombre || 'Cliente', email: cliente.email! },
        movimientosParaEmail,
        saldo,
        organizacionId,
        configEstudio?.telefono || undefined
      )

      if (result.success) {
        toast.success(`Estado de cuenta enviado a ${cliente.email}`)
      } else {
        toast.error(result.error || 'Error al enviar el email')
      }
    } catch (err) {
      toast.error('Error al enviar el email')
      console.error(err)
    } finally {
      setEnviandoEmail(false)
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-5xl p-0 flex flex-col">
          {/* Header con fondo accent */}
          <div className="bg-accent/50 border-b">
            <SheetHeader className="p-6 pb-4">
              <SheetTitle className="flex items-center justify-between gap-3">
                <span className="truncate">Estado de Cuenta - {cliente.nombre}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {onVerFicha && (
                    <Button variant="outline" size="sm" onClick={onVerFicha}>
                      <User className="h-4 w-4 mr-1" />
                      Ver ficha
                    </Button>
                  )}
                  <Badge
                    variant={saldo > 0 ? 'destructive' : 'default'}
                    className="tabular-nums"
                  >
                    Saldo: {formatCurrency(saldo)}
                  </Badge>
                </div>
              </SheetTitle>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Mail className="h-3 w-3 shrink-0" />
                {fechaUltimoEnvio
                  ? <span>Último envío al cliente: {formatDate(fechaUltimoEnvio)}</span>
                  : <span>Sin envíos de estado de cuenta registrados</span>}
              </div>
              <SheetDescription className="sr-only">
                Historial de movimientos y saldo del cliente {cliente.nombre}
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {/* Resumen */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-red-50 border border-red-200 dark:bg-red-950/20 dark:border-red-800 p-3 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Debe</p>
                <p className="text-lg font-bold text-red-600 mt-1 tabular-nums">
                  {formatCurrency(totalDebe)}
                </p>
              </div>
              <div className="rounded-lg bg-green-50 border border-green-200 dark:bg-green-950/20 dark:border-green-800 p-3 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Haber</p>
                <p className="text-lg font-bold text-green-600 mt-1 tabular-nums">
                  {formatCurrency(totalHaber)}
                </p>
              </div>
              <div className={`rounded-lg border p-3 text-center ${
                saldo > 0
                  ? 'bg-red-100 border-red-300 dark:bg-red-950/30 dark:border-red-700'
                  : 'bg-green-100 border-green-300 dark:bg-green-950/30 dark:border-green-700'
              }`}>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Saldo</p>
                <p className={`text-lg font-bold mt-1 tabular-nums ${saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(saldo)}
                </p>
              </div>
            </div>

            {/* Filtros */}
            <div className="flex items-center justify-between gap-3">
              <PeriodPresets value={periodoFiltro} onChange={setPeriodoFiltro} />
              <div className="flex items-center gap-2 shrink-0">
                <Switch id="solo-pendientes" checked={soloPendientes} onCheckedChange={setSoloPendientes} />
                <Label htmlFor="solo-pendientes" className="text-sm cursor-pointer whitespace-nowrap">Solo pendientes</Label>
              </div>
            </div>

            {/* Balance forward */}
            {balanceForward && balanceForward.saldo !== 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/50 text-sm">
                <Info className="h-4 w-4 text-muted-foreground shrink-0" />
                <span>Saldo anterior al {formatDate(balanceForward.fecha)}:</span>
                <span className={`font-bold tabular-nums ${balanceForward.saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(Math.abs(balanceForward.saldo))}
                  {balanceForward.saldo < 0 && <span className="text-xs ml-1">(a favor)</span>}
                </span>
              </div>
            )}

            {/* Acciones */}
            <div className="flex items-center justify-between gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Exportar
                    <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={handleExportar}>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportarPDF} disabled={generandoPDF}>
                    {generandoPDF ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="h-4 w-4 mr-2" />
                    )}
                    Exportar PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setMostrarConfirmEnvio(true)}
                    disabled={enviandoEmail || !cliente.email || !puede.enviarEstadoCuenta}
                  >
                    {enviandoEmail ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Enviar Email
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setMostrarEnvioManual(true)}
                    disabled={!puede.enviarEstadoCuenta}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Marcar como enviado manualmente
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleNuevaDeuda}>
                  <FileText className="h-4 w-4 mr-2" />
                  Registrar Deuda
                </Button>
                <Button size="sm" onClick={handleNuevoPago}>
                  <DollarSign className="h-4 w-4 mr-2" />
                  Registrar Pago
                </Button>
              </div>
            </div>

            {/* Tabla de movimientos */}
            <MovimientosTable
              movimientos={movimientosFiltrados}
              isLoading={isLoading}
              onEditar={handleEditarMovimiento}
              onEliminar={handleEliminarMovimiento}
              onVerComprobante={handleVerComprobante}
              showCliente={false}
              saldoNeto
              canEdit={puede.editarMovimiento}
              canDelete={puede.eliminarMovimiento}
            />

            {movimientosFiltrados.length === 0 && !isLoading && (
              <div className="text-center py-8 text-muted-foreground">
                {soloPendientes ? 'No hay movimientos pendientes' : 'No hay movimientos registrados para este cliente'}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialog para nuevo/editar movimiento */}
      <MovimientoFormDialog
        open={dialogMovimientoOpen}
        onOpenChange={handleCerrarDialogMovimiento}
        movimiento={movimientoAEditar}
        clientes={[cliente]}
        cuentas={cuentas}
        onSubmit={handleSubmitMovimiento}
        isLoading={crearMovimiento.isPending || actualizarMovimiento.isPending}
        clienteIdFijo={cliente.id || undefined}
        tipoFijo={!movimientoAEditar ? 'Deuda' : undefined}
      />

      {/* Dialog inteligente para registrar pago */}
      <RegistrarPagoDialog
        open={mostrarRegistrarPago}
        onOpenChange={setMostrarRegistrarPago}
        clienteId={cliente.id || ''}
        clienteNombre={cliente.nombre || 'Cliente'}
        cuentas={cuentas}
        onSuccess={() => {
          const cId = cliente.id || ''
          queryClient.invalidateQueries({ queryKey: movimientosKeys.porClienteBase(cId) })
          queryClient.invalidateQueries({ queryKey: balanceForwardKeys.porCliente(cId) })
          queryClient.invalidateQueries({ queryKey: ['clientes'] })
        }}
      />

      <MarcarEnvioManualDialog
        open={mostrarEnvioManual}
        onOpenChange={setMostrarEnvioManual}
        clienteNombre={cliente.nombre || 'Cliente'}
        isSubmitting={registrarEnvioManual.isPending}
        onConfirm={handleMarcarEnvioManual}
      />

      {/* ORR-187: confirmación anti-misclick antes de enviar el estado de cuenta */}
      <AlertDialog open={mostrarConfirmEnvio} onOpenChange={setMostrarConfirmEnvio}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Enviar estado de cuenta?</AlertDialogTitle>
            <AlertDialogDescription>
              Se le enviará el estado de cuenta por email a {cliente.nombre}
              {cliente.email ? ` (${cliente.email})` : ''}. ¿Querés continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enviandoEmail}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleEnviarEmail} disabled={enviandoEmail}>
              Enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmación para eliminar */}
      <AlertDialog open={!!movimientoAEliminar} onOpenChange={(open) => !open && setMovimientoAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar movimiento?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="text-sm text-muted-foreground">
                  Esta acción no se puede deshacer. El movimiento será eliminado permanentemente.
                </p>
                {movimientoAEliminar && (
                  <div className="mt-4 p-3 bg-muted rounded-md">
                    <p className="font-medium">{movimientoAEliminar.concepto}</p>
                    <p className="text-sm mt-1">
                      {movimientoAEliminar.tipo === 'Deuda' ? 'Debe' : 'Haber'}: {formatCurrency((movimientoAEliminar.debe ?? 0) + (movimientoAEliminar.haber ?? 0))}
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminarMovimiento.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmarEliminarMovimiento}
              disabled={eliminarMovimiento.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminarMovimiento.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog para ver comprobante adjunto */}
      <ComprobanteViewer
        open={!!comprobanteAVer}
        onOpenChange={(open) => !open && setComprobanteAVer(null)}
        documentoId={comprobanteAVer}
      />
    </>
  )
}
